import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { CanvasStage } from '../components/CanvasStage'
import { ExportDialog } from '../components/ExportDialog'
import { FloorPlanImportDialog } from '../components/FloorPlanImportDialog'
import { GuestDragLayer } from '../components/GuestDragLayer'
import { Inspector, InspectorBody, PlanInspector } from '../components/Inspector'
import { GuestsTab, LeftRail, TablesTab } from '../components/LeftRail'
import { PanelBar } from '../components/PanelBar'
import { PlacingBanner } from '../components/PlacingBanner'
import { Sheet } from '../components/Sheet'
import { TableSettingsDialog } from '../components/TableSettingsDialog'
import { Toast } from '../components/Toast'
import { TopBar } from '../components/TopBar'
import { ZoomControls } from '../components/ZoomControls'
import { useAutosave, whenSaved } from '../hooks/useAutosave'
import { useHotkeys } from '../hooks/useHotkeys'
import { COMPACT_EDITOR, SHORT_EDITOR, useMediaQuery } from '../hooks/useMediaQuery'
import { useTabSync } from '../hooks/useTabSync'
import { type BackupMark, api, backupDue } from '../lib/api'
import { FEATURES } from '../lib/config'
import { tableBounds, unionBounds } from '../lib/geometry'
import { glideCamera } from '../lib/glide'
import type { Catalogue, LocaleCode } from '../lib/i18n'
import { messageForError } from '../lib/i18n/errors'
import { useT } from '../lib/i18n/I18nProvider'
import type { Plan, SeatLabelMode } from '../lib/types'
import { guestSeatIndex, usePlanStore } from '../store/planStore'
import { type EditorPanel, revealCamera, useViewStore } from '../store/viewStore'

interface Props {
  /** Lifted to App so opening a plan can switch the whole app's language. */
  onLocaleChange: (locale: LocaleCode) => void
}

/** Plans already reminded to back up this session — once is a nudge, twice is nagging. */
const remindedToBackUp = new Set<string>()

export function Editor({ onLocaleChange }: Props) {
  const t = useT()
  const { planId } = useParams<{ planId: string }>()
  const navigate = useNavigate()

  const stageRef = useRef<HTMLDivElement>(null)
  const plan = usePlanStore((s) => s.plan)
  const loadPlan = usePlanStore((s) => s.loadPlan)
  const saveStatus = usePlanStore((s) => s.saveStatus)
  const saveError = usePlanStore((s) => s.saveError)
  const showToast = usePlanStore((s) => s.showToast)

  const [loading, setLoading] = useState(true)
  // The raw failure, translated at render. Storing translated prose would put
  // `t` in the load effect's dependencies — and opening a plan whose language
  // differs from the browser's swaps the catalogue, so the effect would run
  // again, re-read, and `loadPlan` would wipe out the edits and undo history
  // of an editor already in use.
  const [loadError, setLoadError] = useState<{ error: unknown } | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [backingUp, setBackingUp] = useState(false)
  // `null` until read; `undefined` inside means "never backed up".
  const [backupMark, setBackupMark] = useState<{ mark: BackupMark | undefined } | null>(null)
  const [floorPlanPages, setFloorPlanPages] = useState(1)
  const [pagesBySeatLabels, setPagesBySeatLabels] = useState<Record<SeatLabelMode, number> | null>(
    null,
  )

  const { saveNow } = useAutosave(Boolean(plan))
  useTabSync(planId ?? null, Boolean(plan))
  useHotkeys(Boolean(plan) && !exportOpen && !importOpen && !settingsOpen)

  // --- the compact editor ------------------------------------------------------
  const compact = useMediaQuery(COMPACT_EDITOR)
  const short = useMediaQuery(SHORT_EDITOR)
  const panel = useViewStore((s) => s.panel)
  const panelExpanded = useViewStore((s) => s.panelExpanded)
  // Only the compact editor reacts to the selection here; on a wide screen these
  // stay constant, so selecting 120 tables does not re-render the whole editor.
  const hasSelection = usePlanStore(
    (s) => compact && (s.selectedTableIds.length > 0 || s.activeSeat !== null),
  )
  const selectionKey = usePlanStore((s) =>
    compact ? (s.activeSeat?.seatId ?? s.selectedTableIds.join(' ')) : '',
  )
  // What the sheet keeps showing while it slides shut.
  const lastPanel = useRef<EditorPanel>('tables')
  if (panel) lastPanel.current = panel

  // Pointer handlers ask the store rather than a render which layout this is.
  useEffect(() => {
    const view = useViewStore.getState()
    view.setCompact(compact)
    if (!compact) {
      view.closePanel()
      view.setSelectMode(false)
    }
  }, [compact])

  // Every plan opens with nothing half-done from the one before.
  useEffect(
    () => () =>
      useViewStore.setState({ panel: null, panelExpanded: false, placing: null, selectMode: false }),
    [planId],
  )

  // The selection sheet has nothing left to show once the selection is gone —
  // deleted from the toolbar, undone, or taken over by another tab.
  useEffect(() => {
    if (panel === 'selection' && !hasSelection) useViewStore.getState().closePanel()
  }, [panel, hasSelection])

  // Once the sheet has risen under what was tapped, bring it back into view
  // above the sheet — by the shortest glide, and not while pulled up to full height.
  useEffect(() => {
    if (!compact || panel !== 'selection') return undefined
    const timer = setTimeout(() => revealSelection(stageRef.current), 300)
    return () => clearTimeout(timer)
  }, [compact, panel, panelExpanded, selectionKey])

  const refreshExportStatus = useCallback(async (id: string) => {
    try {
      const status = await api.exportStatus(id)
      setFloorPlanPages(status.floorPlanPages)
      setPagesBySeatLabels(status.floorPlanPagesBySeatLabels)
    } catch {
      /* non-fatal — the dialog falls back to one page */
    }
  }, [])

  useEffect(() => {
    if (!planId) return
    let cancelled = false
    setBackupMark(null)
    api
      .backupMarks()
      .then((marks) => !cancelled && setBackupMark({ mark: marks.get(planId) }))
      .catch(() => {
        /* the reminder is advisory; without marks it simply stays quiet */
      })
    setLoading(true)
    api
      .getPlan(planId)
      .then((loaded) => {
        if (cancelled) return
        loadPlan(loaded)
        setLoadError(null)
        // A plan carries its own language: opening one switches the editor to
        // it, so the chrome matches the PDF it will print.
        onLocaleChange(loaded.language)
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError({ error })
      })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [planId, loadPlan, onLocaleChange])

  // Shared by the top bar's export button and the inspector's download link,
  // so both refresh the page-count estimate the dialog preselects from.
  const openExport = useCallback(() => {
    if (!FEATURES.pdfExport) return
    setExportOpen(true)
    if (planId) void refreshExportStatus(planId)
  }, [planId, refreshExportStatus])

  /**
   * A backup is of what is *stored*, so unsaved edits are written first — the
   * file must never be older than the canvas the planner is looking at.
   */
  const downloadBackup = useCallback(async () => {
    if (!planId) return
    setBackingUp(true)
    try {
      if (usePlanStore.getState().saveStatus === 'dirty') void saveNow()
      await whenSaved()
      await api.downloadBackup([planId])
      setBackupMark({ mark: (await api.backupMarks()).get(planId) })
      showToast(t.common.backupSaved, 'success')
    } catch (error) {
      showToast(messageForError(t, error), 'warn')
    } finally {
      setBackingUp(false)
    }
  }, [planId, saveNow, showToast, t])

  const due = Boolean(plan && backupMark && backupDue(plan, backupMark.mark))

  // Once per plan per session, when a backup falls due while it is open (or it
  // opens already due). The top bar keeps a quieter mark for as long as it is.
  useEffect(() => {
    if (!due || !planId || remindedToBackUp.has(planId)) return
    remindedToBackUp.add(planId)
    showToast(t.editor.backupReminder, 'info')
  }, [due, planId, showToast, t])

  if (loading) {
    return (
      <div className="editor-loading">
        <motion.div
          className="loader-ring"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
        />
        <p>{t.editor.loading}</p>
      </div>
    )
  }

  if (loadError || !plan) {
    return (
      <div className="editor-loading">
        <h2>{t.editor.loadFailedTitle}</h2>
        <p>{loadError ? messageForError(t, loadError.error, t.errors.openPlan) : t.editor.loadFailedBody}</p>
        <button type="button" className="btn btn-primary" onClick={() => navigate('/plans')}>
          {t.common.myPlans}
        </button>
      </div>
    )
  }

  const topBar = (
    <TopBar
      plan={plan}
      onOpenSettings={() => setSettingsOpen(true)}
      onBackup={() => void downloadBackup()}
      backingUp={backingUp}
      backupDue={due}
      onExport={openExport}
      compact={compact}
      panels={compact && short ? <PanelBar plan={plan} placement="top" /> : undefined}
    />
  )

  const inspectorProps = {
    plan,
    onExport: openExport,
    onBackup: () => void downloadBackup(),
    backingUp,
    backupMark,
  }

  return (
    <div
      className={[
        'editor',
        compact ? 'editor--compact' : '',
        compact && short ? 'editor--short' : '',
        compact && panel !== null ? 'has-sheet' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {topBar}

      <AnimatePresence>
        {saveStatus === 'conflict' && (
          <motion.div
            className="banner banner--warn"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <span>{saveError}</span>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => location.reload()}>
              {t.common.reload}
            </button>
          </motion.div>
        )}
        {saveStatus === 'error' && (
          <motion.div
            className="banner banner--error"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <span>
              {saveError ?? t.editor.notSaving} {t.editor.keepTrying}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {compact ? (
        <>
          <div className="editor-body">
            <main className="editor-canvas" ref={stageRef}>
              <CanvasStage plan={plan} />
              <ZoomControls plan={plan} stageRef={stageRef} />
              <PlacingBanner plan={plan} />
            </main>

            <Sheet
              open={panel !== null}
              side={short}
              expanded={panelExpanded}
              title={sheetTitle(t, plan, panel ?? lastPanel.current)}
              titleHidden={(panel ?? lastPanel.current) === 'selection'}
              onClose={() => useViewStore.getState().closePanel()}
              onExpandedChange={(expanded) => useViewStore.getState().setPanelExpanded(expanded)}
            >
              <SheetPanel
                panel={panel ?? lastPanel.current}
                stageRef={stageRef}
                onImportFloorPlan={() => setImportOpen(true)}
                inspectorProps={inspectorProps}
              />
            </Sheet>
          </div>

          {!short && <PanelBar plan={plan} placement="bottom" />}
        </>
      ) : (
        <div className="editor-body">
          <LeftRail
            plan={plan}
            stageRef={stageRef}
            onImportFloorPlan={() => setImportOpen(true)}
          />

          <main className="editor-canvas" ref={stageRef}>
            <CanvasStage plan={plan} />
            <ZoomControls plan={plan} stageRef={stageRef} />
            <PlacingBanner plan={plan} />
          </main>

          <Inspector {...inspectorProps} />
        </div>
      )}

      <GuestDragLayer plan={plan} />
      <Toast />

      <TableSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        plan={plan}
      />

      {FEATURES.floorPlanImport && (
        <FloorPlanImportDialog open={importOpen} onClose={() => setImportOpen(false)} plan={plan} />
      )}

      {FEATURES.pdfExport && (
        <ExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          plan={plan}
          floorPlanPages={floorPlanPages}
          pagesBySeatLabels={pagesBySeatLabels}
        />
      )}
    </div>
  )
}

/** The sheet's heading: what it holds, or — for the selection — what was tapped. */
function sheetTitle(t: Catalogue, plan: Plan, panel: EditorPanel): string {
  if (panel === 'tables') return t.rail.tabTables
  if (panel === 'guests') return t.rail.tabGuests
  if (panel === 'plan') return t.inspector.thisPlan
  const { activeSeat, selectedTableIds } = usePlanStore.getState()
  if (activeSeat) {
    const table = plan.tables.find((entry) => entry.id === activeSeat.tableId)
    const seat = table?.seats.find((entry) => entry.id === activeSeat.seatId)
    if (table && seat) return t.rail.seatAt(table.name, seat.position + 1)
  }
  if (selectedTableIds.length === 1) {
    return plan.tables.find((entry) => entry.id === selectedTableIds[0])?.name ?? t.rail.tabTables
  }
  return t.inspector.tablesSelected(selectedTableIds.length)
}

/** One panel's contents in the sheet — the same components the wide editor lays out in columns. */
function SheetPanel({
  panel,
  stageRef,
  onImportFloorPlan,
  inspectorProps,
}: {
  panel: EditorPanel
  stageRef: React.RefObject<HTMLDivElement>
  onImportFloorPlan: () => void
  inspectorProps: React.ComponentProps<typeof InspectorBody>
}) {
  const { plan } = inspectorProps
  const seatIndex = useMemo(() => guestSeatIndex(plan), [plan])

  if (panel === 'tables') {
    return <TablesTab plan={plan} stageRef={stageRef} onImportFloorPlan={onImportFloorPlan} />
  }
  if (panel === 'guests') {
    const seatedTotal = plan.guests.filter((guest) => seatIndex.has(guest.id)).length
    return <GuestsTab plan={plan} seatIndex={seatIndex} seatedTotal={seatedTotal} />
  }
  return (
    <div className="sheet-scroll scroll-y">
      {panel === 'plan' ? <PlanInspector {...inspectorProps} /> : <InspectorBody {...inspectorProps} />}
    </div>
  )
}

/**
 * Glide what the planner just tapped back into sight when the sheet has come
 * up over it: the shortest pan that puts it inside the canvas that is left.
 */
function revealSelection(stage: HTMLElement | null): void {
  const { plan, activeSeat, selectedTableIds } = usePlanStore.getState()
  if (!plan || !stage) return
  const ids = activeSeat ? [activeSeat.tableId] : selectedTableIds
  const bounds = unionBounds(
    plan.tables
      .filter((table) => ids.includes(table.id))
      .map((table) => tableBounds(table.shape, table.seatCount, table.x, table.y, table.rotation)),
  )
  const rect = stage.getBoundingClientRect()
  // Pulled up to full height, there is no room left to reveal it in.
  if (!bounds || rect.height < 120) return
  const view = useViewStore.getState()
  // Room above for the table's floating toolbar; a little everywhere else.
  const margins = { top: 76, right: 16, bottom: 16, left: 16 }
  const next = revealCamera(view, bounds, { width: rect.width, height: rect.height }, margins)
  if (next.x !== view.x || next.y !== view.y) glideCamera(next)
}
